/**
 * Utility functions for generating random vibrant gradient avatars
 */

// Array of vibrant gradient color pairs
const gradientColors = [
  ['#FF5F6D', '#FFC371'], // Warm Flame
  ['#2980B9', '#6DD5FA'], // Sky Blue
  ['#8E2DE2', '#4A00E0'], // Royal Purple
  ['#F953C6', '#B91D73'], // Sweet Pink
  ['#43CEA2', '#185A9D'], // Deep Teal
  ['#FC466B', '#3F5EFB'], // Plum Plate
  ['#C33764', '#1D2671'], // Love Couple
  ['#00F260', '#0575E6'], // Spring Green
  ['#FDBB2D', '#22C1C3'], // Malibu Beach
  ['#FF9966', '#FF5E62'], // Orange Coral
  ['#7F00FF', '#E100FF'], // Vivid Purple
  ['#396AFC', '#2948FF'], // Deep Blue
  ['#FBAB7E', '#F7CE68'], // Juicy Orange
  ['#85FFBD', '#FFFB7D'], // Sunny Morning
  ['#FF9A9E', '#FECFEF'], // Young Passion
  ['#A9C9FF', '#FFBBEC'], // Cotton Candy
];

/**
 * Generate a consistent avatar URL based on the user's name
 * Returns either a gradient SVG or the original avatar URL if available
 */
export function getAvatarUrl(
  firstName: string,
  lastName: string,
  originalAvatarUrl?: string | null
): string {
  // If user has an avatar URL already, use it
  if (originalAvatarUrl) return originalAvatarUrl;

  // Create a hash from the full name for consistent gradients per user
  const fullName = `${firstName} ${lastName}`;
  const hash = stringToHash(fullName);
  
  // Select a gradient based on the hash
  const gradientIndex = Math.abs(hash) % gradientColors.length;
  const [color1, color2] = gradientColors[gradientIndex];
  
  // Generate the SVG for the gradient
  const svg = generateGradientSvg(color1, color2, getInitials(firstName, lastName));
  
  // Convert SVG to data URL
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
/**
 * Generate an SVG with a linear gradient and initials
 */
function generateGradientSvg(color1: string, color2: string, initials: string): string {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
      <defs>
        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${color1}" />
          <stop offset="100%" stop-color="${color2}" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="16" fill="url(#gradient)" />
      <text x="16" y="21" font-family="Inter, Arial, sans-serif" font-size="14px" font-weight="600" 
        text-anchor="middle" fill="white">${initials}</text>
    </svg>
  `;
}

/**
 * Get initials from first and last name
 */
function getInitials(firstName: string, lastName: string): string {
  const first = firstName.trim();
  const last = lastName.trim();
  
  if (first && last) {
    return (first[0] + last[0]).toUpperCase();
  } else if (first) {
    return first.length > 1 
      ? (first[0] + first[1]).toUpperCase() 
      : first[0].toUpperCase();
  } else if (last) {
    return last.length > 1 
      ? (last[0] + last[1]).toUpperCase() 
      : last[0].toUpperCase();
  }
  
  // Fallback
  return "?";
}

/**
 * Create a numeric hash from a string
 */
function stringToHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
} 